import React, { useContext } from 'react';
import Box from '@amzn/meridian/box';
import { FormattedMessage } from 'react-intl';
import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import Link from '@amzn/meridian/link';
import { adjustContactCounts, DISPLAY_MAX_CONTACTS } from './Util/helper';
import { renderUrl, urls } from '../../../Util';
import { ContactReviewStatusOptions } from '../../../api/Outlier/schema';
import { AssociateCardSummary } from '../../../Types';
import { StateContext } from '../../../GlobalStateStore';
import { ARCFilters } from './Types/types';
import { ConsolidatedContact } from '../../../api/CoachingNotes/schema';
import { useNavigate } from 'react-router';
import PerformancePortalContext from '../../PerformancePortalContext';

export interface ExpandedContactCardProps {
  associateCardSummary: AssociateCardSummary;
  displayIndex: number;
  category: string;
  recomContacts: ConsolidatedContact[];
}

const ExpandedContactCard: React.FC<ExpandedContactCardProps> = ({
  associateCardSummary,
  displayIndex,
  category,
  recomContacts,
}: ExpandedContactCardProps): React.ReactElement => {
  const globalState = useContext(StateContext);
  const { clearPerformancePortalContextValue } = useContext(
    PerformancePortalContext
  );
  const { inspireOptions } = globalState.state;
  const { isTm, requesterUserID, requesterUsername } = inspireOptions.data;

  const navigate = useNavigate();

  const contactsSummary =
    category === ARCFilters.OPPORTUNITIES
      ? associateCardSummary.opportunities
      : associateCardSummary.kudos;

  const overlapSet = new Set(recomContacts.map(item => item.contactID));

  const [notReviewedContacts, totalCounts] = adjustContactCounts(
    contactsSummary,
    recomContacts,
    displayIndex
  );

  return (
    <>
      {contactsSummary &&
        contactsSummary.labelAndCount[displayIndex].category && (
          <Box type='outline'>
            <Column spacingInset='400'>
              <Text type='h100'>
                {contactsSummary.labelAndCount[displayIndex].category}
              </Text>
              <Text type='b200'>
                {notReviewedContacts === 0 ? (
                  <FormattedMessage
                    id='cs_perfportal_arc_contact_all_reviewed_description'
                    defaultMessage='All recommended contacts have been reviewed'
                  />
                ) : (
                  <FormattedMessage
                    id='cs_perfportal_arc_opportunity_contact_description'
                    defaultMessage='{notReviewedContactCounts} of {totalContactCounts} recommended contacts need review'
                    values={{
                      notReviewedContactCounts: notReviewedContacts,
                      totalContactCounts: totalCounts,
                    }}
                  />
                )}
              </Text>
              <Column spacing='200' spacingInset='200'>
                {contactsSummary.labelAndCount[displayIndex]?.contacts
                  ?.filter(
                    contactItem =>
                      contactItem.contactStatus !==
                        ContactReviewStatusOptions.REVIEWED &&
                      contactItem.contactStatus !==
                        ContactReviewStatusOptions.DELIVERED
                  )
                  .sort((a, b) => {
                    // Prioritize based on overlap with contact list from Recommendation Tab
                    const aHasOverlap = overlapSet.has(a.contact) ? 1 : 0;
                    const bHasOverlap = overlapSet.has(b.contact) ? 1 : 0;
                    return bHasOverlap - aHasOverlap; // Higher overlap comes first
                  })
                  .slice(0, DISPLAY_MAX_CONTACTS)
                  .map(contactItem => (
                    <Text type='b200' key={contactItem.contact}>
                      <Link
                        href={renderUrl(
                          `${urls.contactReview}/${associateCardSummary.userID}/${contactItem.contact}`
                        )}
                        onClick={href => {
                          // context is also cleared on top level nav change in PerformancePortalLandingProps
                          // theoretically, this will not do anything, but included here as a backstop incase the user ends up on this page via an alternate path
                          clearPerformancePortalContextValue();
                          navigate(href);
                        }}
                        type='secondary'
                        data-csa-c-type='link'
                        data-csa-c-slot-id='arc-expanded-open-contact-review-link-slot'
                        data-csa-c-content-id={`arc-expanded-open-contact-review-for-${associateCardSummary.userID}-${contactItem.contact}`}
                        data-csa-c-tag1='Team'
                        data-csa-c-tag2='Associates'
                        data-csa-c-tag3='ExpandedView'
                        data-csa-c-tag4={
                          category === ARCFilters.OPPORTUNITIES
                            ? 'Opportunities'
                            : 'Kudos'
                        }
                        data-csa-c-profile-id={requesterUserID}
                        data-csa-c-username={requesterUsername}
                        data-csa-c-is-tm={isTm}
                      >
                        {contactItem.contact}
                      </Link>
                    </Text>
                  ))}
                <Text type='b200'>
                  <Link
                    href={renderUrl(
                      `${urls.coachingNotes}/${associateCardSummary.userID}?category=${contactsSummary.labelAndCount[displayIndex].category}#nav-recommended`
                    )}
                    onClick={navigate}
                    type='secondary'
                    data-csa-c-type='link'
                    data-csa-c-slot-id='arc-expanded-open-recommendations-link-slot'
                    data-csa-c-content-id={`arc-expanded-open-recommendations-for-${associateCardSummary.userID}-category-${contactsSummary.labelAndCount[displayIndex].category}`}
                    data-csa-c-tag1='Team'
                    data-csa-c-tag2='Associates'
                    data-csa-c-tag3='ExpandedView'
                    data-csa-c-tag4={
                      category === ARCFilters.OPPORTUNITIES
                        ? 'Opportunities'
                        : 'Kudos'
                    }
                    data-csa-c-profile-id={requesterUserID}
                    data-csa-c-username={requesterUsername}
                    data-csa-c-is-tm={isTm}
                  >
                    <FormattedMessage
                      id='cs_perfportal_arc_see_all_recommendations'
                      defaultMessage='See all recommendations'
                    />
                  </Link>
                </Text>
              </Column>
            </Column>
          </Box>
        )}
    </>
  );
};

export default ExpandedContactCard;
